import React, { useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { IoRecording, IoBulbOutline } from 'react-icons/io5';
import { useLanguage } from '../../i18n/LanguageContext';

// The live transcript, as a tab of the side panel. Everything it renders
// arrives over the data channel from the backend's transcript bot - there is
// no request path here at all, because a guest has nothing to authenticate a
// request with.
export default function MeetingTranscript({ transcript, isHost, onToggle, busy }) {
    const { t } = useLanguage();
    const listRef = useRef(null);

    const partials = Object.entries(transcript.partials || {}).map(([userId, partial]) => ({ userId, ...partial }));
    const running = transcript.status === 'active' || transcript.status === 'connecting';
    // Keyed on what is actually on screen rather than a length, so a partial
    // growing in place still scrolls the newest words into view.
    const tailKey = `${transcript.segments.length}:${partials.map((p) => p.text.length).join(',')}`;

    // Scrolls the list itself, not a sentinel - see the same note in
    // MeetingSidePanel's ChatTab for why scrollIntoView is the wrong tool in a
    // layout made of overflow:hidden panels.
    useEffect(() => {
        const list = listRef.current;
        if (list) list.scrollTop = list.scrollHeight;
    }, [tailKey]);

    return (
        <>
            <div className="MeetTranscriptBar">
                <span className={`MeetTranscriptStatus ${running ? 'MeetTranscriptStatusLive' : ''}`}>
                    <IoRecording />
                    {transcript.status === 'connecting' && t('meeting.transcript.starting')}
                    {transcript.status === 'active' && t('meeting.transcript.live')}
                    {transcript.status === 'error' && t('meeting.transcript.error')}
                    {transcript.status === 'idle' && t('meeting.transcript.off')}
                </span>
                {/* Only the host gets the switch; everyone gets the status, so
                    nobody is transcribed without being able to see it. */}
                {isHost && (
                    <button
                        className={`MeetTranscriptToggle ${running ? 'cancelColor' : 'primaryColor'}`}
                        onClick={() => onToggle(!running)}
                        disabled={busy || transcript.status === 'connecting'}
                    >
                        {running ? t('meeting.transcript.stop') : t('meeting.transcript.start')}
                    </button>
                )}
            </div>

            <div className="MeetTranscriptLines" ref={listRef}>
                {transcript.segments.length === 0 && partials.length === 0 && (
                    <p className="MeetSideEmpty">
                        {running ? t('meeting.transcript.listening') : t('meeting.transcript.empty')}
                    </p>
                )}
                {transcript.segments.map((segment) => (
                    <div key={segment.seq} className="MeetTranscriptLine">
                        <b className="MeetTranscriptSpeaker">{segment.username}</b>
                        <span className="MeetTranscriptText">{segment.text}</span>
                    </div>
                ))}
                {/* Not-yet-finalized speech, styled softer so it reads as
                    provisional next to the settled lines above - those will
                    not change again, these will. */}
                {partials.map((partial) => (
                    <div key={partial.userId} className="MeetTranscriptLine MeetTranscriptLinePartial">
                        <b className="MeetTranscriptSpeaker">{partial.username}</b>
                        <span className="MeetTranscriptText">{partial.text}</span>
                    </div>
                ))}
            </div>

            {(transcript.summaryStatus || transcript.summary) && (
                <div className="MeetTranscriptSummary">
                    <span className="MeetTranscriptSummaryTitle"><IoBulbOutline /> {t('meeting.transcript.summaryTitle')}</span>
                    {transcript.summaryStatus === 'pending' && <p className="descGray">{t('meeting.transcript.summaryPending')}</p>}
                    {transcript.summaryStatus === 'skipped' && <p className="descGray">{t('meeting.transcript.summarySkipped')}</p>}
                    {transcript.summaryStatus === 'failed' && <p className="descGray">{t('meeting.transcript.summaryFailed')}</p>}
                    {transcript.summary && (
                        <div className="MeetTranscriptSummaryBody">
                            <ReactMarkdown>{transcript.summary}</ReactMarkdown>
                        </div>
                    )}
                </div>
            )}
        </>
    );
}
